<template>
  <v-container>
    <!-- Welcome band -->
    <div class="welcome">
      <div class="welcome__row">
        <div class="welcome__badge">
          <v-icon size="26">mdi-home-outline</v-icon>
        </div>
        <div class="min-width-0 flex-grow-1">
          <div class="welcome__title">{{ $filters.capitalize($t("menu.home")) }}</div>
          <div class="welcome__text">{{ $filters.capitalize($t("home.welcome")) }}</div>
        </div>
        <RiskButton class="welcome__risk"></RiskButton>
      </div>
    </div>

    <!-- Quick access: the main pages -->
    <div class="section-title">
      <v-icon size="small" class="mr-2" color="primary">mdi-view-grid-outline</v-icon>
      {{ $filters.capitalize($t("home.quick_access")) }}
    </div>
    <div class="tiles">
      <router-link
        v-for="tile in tiles"
        :key="tile.route"
        :to="{ name: tile.route, query: tile.query || {} }"
        class="tile"
        :class="`tile--${tile.color}`"
      >
        <div class="tile__badge"><v-icon size="22">{{ tile.icon }}</v-icon></div>
        <div class="min-width-0">
          <div class="tile__name">{{ $filters.capitalize($t(tile.label)) }}</div>
          <div class="tile__hint">{{ $t(tile.hint) }}</div>
        </div>
        <v-icon size="small" class="tile__arrow">mdi-chevron-right</v-icon>
      </router-link>
    </div>

    <!-- Shortcuts: direct links to a region, a warehouse, a producer -->
    <div class="section-title">
      <v-icon size="small" class="mr-2" color="primary">mdi-lightning-bolt-outline</v-icon>
      {{ $filters.capitalize($t("home.shortcuts")) }}
    </div>
    <div class="shortcuts">
      <v-card flat class="shortcut">
        <div class="shortcut__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-calendar-month-outline</v-icon>
          <span>{{ $filters.capitalize($t("home.scheduler")) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ plannings.length }}</v-chip>
        </div>
        <div class="shortcut__chips">
          <v-chip
            v-for="item in plannings"
            :key="item.id"
            size="small"
            variant="tonal"
            color="primary"
            link
            @click="redirect_to('scheduler', { regions: [item.id] })"
          >
            {{ $filters.capitalize(item.name) }}
          </v-chip>
          <span v-if="!plannings.length" class="text-caption text-disabled">&ndash;</span>
        </div>
      </v-card>

      <v-card flat class="shortcut">
        <div class="shortcut__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-warehouse</v-icon>
          <span>{{ $filters.capitalize($t("home.stockage")) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ stockages.length }}</v-chip>
        </div>
        <div class="shortcut__chips">
          <v-chip
            v-for="item in stockages"
            :key="item.stockage_id"
            size="small"
            variant="tonal"
            color="primary"
            link
            @click="redirect_to('part_view', { stockage_id: item.stockage_id })"
          >
            {{ $filters.capitalize(item.stockage_name) }}
          </v-chip>
          <span v-if="!stockages.length" class="text-caption text-disabled">&ndash;</span>
        </div>
      </v-card>

      <v-card flat class="shortcut">
        <div class="shortcut__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-clipboard-text-outline</v-icon>
          <span>{{ $filters.capitalize($t("home.producer")) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ producers.length }}</v-chip>
        </div>
        <div class="shortcut__chips">
          <v-chip
            v-for="item in producers"
            :key="item.pk"
            size="small"
            variant="tonal"
            color="primary"
            link
            @click="redirect_to('intervention_reports', { producer_id: item.pk })"
          >
            {{ $filters.capitalize(item.display_name) }}
          </v-chip>
          <span v-if="!producers.length" class="text-caption text-disabled">&ndash;</span>
        </div>
      </v-card>

      <v-card v-if="isWindProfil" flat class="shortcut">
        <div class="shortcut__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-boom-gate-outline</v-icon>
          <span>{{ $filters.capitalize($t("home.automatons")) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ automatons_list.length }}</v-chip>
        </div>
        <div class="shortcut__chips">
          <v-chip
            v-for="item in automatons_list"
            :key="item.pk"
            size="small"
            variant="tonal"
            color="primary"
            link
            @click="redirect_to('automaton', { producer_id: item.pk })"
          >
            {{ $filters.capitalize(item.display_name) }}
          </v-chip>
          <span v-if="!automatons_list.length" class="text-caption text-disabled">&ndash;</span>
        </div>
      </v-card>

      <v-card flat class="shortcut">
        <div class="shortcut__head">
          <v-icon size="small" class="mr-2" color="error">mdi-bell-alert</v-icon>
          <span>{{ $filters.capitalize($t("home.alarms")) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ producer_alarms.length }}</v-chip>
        </div>
        <div class="shortcut__chips">
          <v-chip
            v-for="item in producer_alarms"
            :key="item.pk"
            size="small"
            variant="tonal"
            color="error"
            link
            @click="redirect_to('alarms', { producer_id: item.pk })"
          >
            {{ $filters.capitalize(item.display_name) }}
          </v-chip>
          <span v-if="!producer_alarms.length" class="text-caption text-disabled">&ndash;</span>
        </div>
      </v-card>

      <v-card flat class="shortcut">
        <div class="shortcut__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-cart-outline</v-icon>
          <span>{{ $filters.capitalize($t("home.purchase")) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ (stockages_x_producer_type || []).length }}</v-chip>
        </div>
        <div class="shortcut__chips">
          <v-chip
            v-for="item in stockages_x_producer_type"
            :key="item.item_id"
            size="small"
            variant="tonal"
            color="primary"
            link
            @click="redirect_to('purchase_view', { stockage_id: item.stockage_id, producer_type_id: item.producer_type_id })"
          >
            {{ $filters.capitalize(item.full_name) }}
          </v-chip>
          <span v-if="!(stockages_x_producer_type || []).length" class="text-caption text-disabled">&ndash;</span>
        </div>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import RiskButton from "@/components/RiskButton";
import { defaultRegionsForUser, rememberUserRegions } from "@/functions";

export default {
  components: { RiskButton },

  computed: {
    isWindProfil() {
      return this.$store.getters.isWindProfil;
    },

    stockages_x_producer_type() {
      return this.$store.state.stockages.x_prod_type;
    },

    // Stockages the user is assigned to. Staff receive every stockage from the
    // backend, so the assignment is checked here on the stockage's user ids.
    assignedStockages() {
      const userId = this.$store.state.user ? this.$store.state.user.id : null;
      return this.my_stockages.filter((s) => Array.isArray(s.user_ids) && s.user_ids.includes(userId));
    },


    // The user's default stockage: the first assigned one, by name
    defaultStockage() {
      const mine = [...this.assignedStockages].sort((a, b) => String(a.name).localeCompare(String(b.name)));
      return mine.length ? mine[0] : null;
    },

    defaultStockageId() {
      return this.defaultStockage ? this.defaultStockage.pk : null;
    },

    // The scheduler opens on the user's regions (region of the assigned stockages,
    // or the region named like the stockage when its region field is empty)
    defaultRegions() {
      const userId = this.$store.state.user ? this.$store.state.user.id : null;
      return defaultRegionsForUser(this.my_stockages, userId, this.all_regions);
    },

    // Scheduler shortcuts: only the regions of the user's stockages (all of them when none is assigned)
    // Shortcut chips list every region; the user's own regions only drive the tile
    plannings() {
      return this.all_regions;
    },

    // Shortcut chips list every stockage; the user's own stockage only drives the tile
    stockages() {
      return Array.from(
        new Map(
          (this.stockages_x_producer_type || []).map((x) => [x.stockage_name, { stockage_name: x.stockage_name, stockage_id: x.stockage_id }])
        ).values()
      );
    },

    // The main pages, in the order they are used day to day
    tiles() {
      return [
        { route: "wind_production", icon: "mdi-wind-turbine", color: "blue", label: "menu.wind_production", hint: "home.tiles.wind" },
        { route: "solar_production", icon: "mdi-white-balance-sunny", color: "amber", label: "menu.solar_production", hint: "home.tiles.solar" },
        { route: "part_view", icon: "mdi-barcode", color: "green", label: "home.tiles.stock_title", hint: "home.tiles.stock",
          query: this.defaultStockageId ? { stockage_id: this.defaultStockageId } : {} },
        { route: "alarms", icon: "mdi-bell-alert", color: "red", label: "alarms.label", hint: "home.tiles.alarms" },
        { route: "limitation_view", icon: "mdi-tortoise", color: "brown", label: "limitations.label", hint: "home.tiles.limitations" },
        { route: "prod_availability_month", icon: "mdi-chart-box-outline", color: "teal", label: "home.tiles.availability_title", hint: "home.tiles.availability" },
        { route: "scheduler", icon: "mdi-calendar-month-outline", color: "purple", label: "menu.scheduler", hint: "home.tiles.scheduler",
          query: this.defaultRegions.length ? { regions: this.defaultRegions } : {} },
        { route: "maintenance_view", icon: "mdi-wrench-outline", color: "grey", label: "menu.maintenance", hint: "home.tiles.maintenance" },
        { route: "todo_view", icon: "mdi-check-circle-outline", color: "teal", label: "todos.label", hint: "home.tiles.todos" }
      ];
    }
  },

  created() {
    this.$store.dispatch("stockages/getRegions").then((regions) => {
      this.all_regions = regions;
    });
    this.$store.dispatch("stockages/getMine").then((stockages) => {
      this.my_stockages = Array.isArray(stockages) ? stockages : [];
      const userId = this.$store.state.user ? this.$store.state.user.id : null;
      rememberUserRegions(userId, this.defaultRegions);
    });
    if (!this.stockages_x_producer_type || this.stockages_x_producer_type.length === 0) {
      this.$store.dispatch("stockages/getXProdType");
    }
    this.$store.dispatch("producers/getMine").then((producers) => {
      this.producers = producers;
      if (this.isWindProfil) {
        this.automatons_list = producers.filter((p) => p.has_automate);
      }
      this.producer_alarms = producers.filter((p) => p.is_alarm_compatible);
    });
  },

  methods: {
    redirect_to(name, query) {
      this.$router.push({ name: name, query: query });
    }
  },

  unmounted() {
    this.all_regions = [];
    this.my_stockages = [];
    this.producers = [];
    this.producer_alarms = [];
    this.automatons_list = [];
  },

  data: () => ({
    all_regions: [],
    my_stockages: [],
    producers: [],
    producer_alarms: [],
    automatons_list: []
  })
};
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}

/* Welcome band, the look of the page headers */
.welcome {
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.welcome::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #1f9fd1 40%, #43a047 70%, #f6b73c 100%);
}
.welcome__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
}
.welcome__badge {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #dff3f9;
  color: #0093bf;
}
.welcome__title {
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.2;
}
.welcome__text {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}
.welcome__risk {
  flex-shrink: 0;
}
.welcome__risk :deep(.v-btn) {
  margin-bottom: 0 !important;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 18px 4px 8px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.55);
}

/* Quick access tiles */
.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.tile:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}
.tile__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tile--blue .tile__badge {
  background: #dff3f9;
  color: #0093bf;
}
.tile--amber .tile__badge {
  background: #fff3d6;
  color: #e08a00;
}
.tile--green .tile__badge {
  background: #e3f4e4;
  color: #2e7d32;
}
.tile--red .tile__badge {
  background: #fde7e7;
  color: #d32f2f;
}
.tile--brown .tile__badge {
  background: #efe9e4;
  color: #6b5b4f;
}
.tile--teal .tile__badge {
  background: #dcf5ef;
  color: #00897b;
}
.tile--purple .tile__badge {
  background: #ece6f7;
  color: #6a3fb5;
}
.tile--grey .tile__badge {
  background: #eeeeee;
  color: #616161;
}
.tile__name {
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tile__hint {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tile__arrow {
  margin-left: auto;
  color: rgba(0, 0, 0, 0.26);
  transition: color 0.15s ease, transform 0.15s ease;
}
.tile:hover .tile__arrow {
  color: rgb(var(--v-theme-primary));
  transform: translateX(2px);
}

/* Shortcut cards */
.shortcuts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.shortcut {
  padding: 12px 16px 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.shortcut__head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
}
.shortcut__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

@media (max-width: 1000px) {
  .tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 700px) {
  .tiles,
  .shortcuts {
    grid-template-columns: 1fr;
  }
  .welcome__row {
    flex-wrap: wrap;
  }
}
</style>
